import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Alert,
  Box,
  Grid,
  CircularProgress,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { CalendarSlot } from '../../../types/calendar.types';
import { API_BASE_URL, authFetch } from '../../../config/api.config';
import { istUnsichtbarFuerGaeste, HINWEIS_UNSICHTBAR } from '../../../utils/gaestesichtbarkeit';

// Tour-Type Interface für API Response
interface TourType {
  id: number;
  name: string;
  code?: string;
}

interface SlotEditorProps {
  slot: CalendarSlot | null;
  open: boolean;
  onClose: () => void;
  onSave: (updatedSlot: CalendarSlot) => void;  onDelete?: (slotId: number) => void;
}

const SlotEditor: React.FC<SlotEditorProps> = ({ slot, open, onClose, onSave, onDelete }) => {
  const [formData, setFormData] = useState<Partial<CalendarSlot>>({
    tourType: 'STANDARD',
    maxCapacity: 140,
    minCapacity: 30,
    pricePerPerson: 48.50,
    isBlocked: false,
    specialNotes: '',
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [saving, setSaving] = useState(false);
  // T-034 (02.09.2026): fachliche Ablehnung des Backends (z. B. Zeitkollision) im Dialog anzeigen
  const [saveError, setSaveError] = useState<string | null>(null);
  // T-154 (v1.8.44): Schalter "Beim Speichern für Gäste sichtbar machen" (nur bei unsichtbaren Terminen)
  const [sichtbarMachen, setSichtbarMachen] = useState(false);

  // Tour-Typen aus der API laden
  const [tourTypes, setTourTypes] = useState<TourType[]>([]);
  const [loadingTourTypes, setLoadingTourTypes] = useState(false);

  // Tour-Typen beim Öffnen des Dialogs laden
  useEffect(() => {
    if (open) {
      loadTourTypes();
    }
  }, [open]);

  const loadTourTypes = async () => {
    setLoadingTourTypes(true);
    try {
      // T-009: authFetch setzt die Auth-Header und refresht bei 401 automatisch
      const response = await authFetch(`${API_BASE_URL}/tour-types`, {});

      if (response.ok) {
        const data = await response.json();
        // API könnte { items: [...] } oder direkt [...] zurückgeben
        const types = data.items || data || [];
        setTourTypes(types);
      } else {
        console.error('Failed to load tour types:', response.status);
      }
    } catch (error) {
      console.error('Error loading tour types:', error);
    } finally {
      setLoadingTourTypes(false);
    }
  };

  // Initialize form with slot data
  useEffect(() => {
    setSichtbarMachen(false);
    if (slot) {
      setFormData({
        ...slot,
        time: slot.time.substring(0, 5), // Convert "14:00:00" to "14:00"
      });
    } else {
      // Reset for new slot
      setFormData({
        tourType: 'STANDARD',
        maxCapacity: 140,
        minCapacity: 30,
        pricePerPerson: 48.50,
        isBlocked: false,
        specialNotes: '',
      });
    }
    setErrors({});
  }, [slot, open]);
  // Handle input changes
  const handleChange = (field: keyof CalendarSlot) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
  ) => {
    const value = event.target.type === 'checkbox' 
      ? event.target.checked 
      : event.target.value;
    
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field
    setErrors(prev => ({
      ...prev,
      [field]: '',
    }));
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.time) {
      newErrors.time = 'Zeit ist erforderlich';
    }

    if (!formData.tourType) {
      newErrors.tourType = 'Tour-Typ ist erforderlich';
    }

    if (formData.minCapacity && formData.minCapacity < 1) {
      newErrors.minCapacity = 'Mindestkapazität muss größer als 0 sein';
    }

    if (formData.maxCapacity && formData.maxCapacity < 1) {
      newErrors.maxCapacity = 'Maximalkapazität muss größer als 0 sein';
    }

    if (formData.minCapacity && formData.maxCapacity && formData.minCapacity > formData.maxCapacity) {
      newErrors.minCapacity = 'Mindestkapazität kann nicht größer als Maximalkapazität sein';
    }

    if (formData.pricePerPerson && formData.pricePerPerson < 0) {
      newErrors.pricePerPerson = 'Preis kann nicht negativ sein';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    if (!slot) {
      console.error('Creating new slots not supported in this component');
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      const updatedSlot: CalendarSlot = {
        ...slot,
        ...formData,
        time: formData.time ? `${formData.time}:00` : slot.time,
        // T-154 (v1.8.44): nur mitsenden, wenn der Schalter gesetzt ist (Backend wendet es nach den übrigen Feldern an)
        fuerGaesteSichtbarMachen: sichtbarMachen && istUnsichtbarFuerGaeste(slot) ? true : undefined,
      } as CalendarSlot;

      await onSave(updatedSlot);
    } catch (error) {
      console.error('Error saving slot:', error);
      setSaveError(error instanceof Error ? error.message : 'Fehler beim Speichern des Termins');
    } finally {
      setSaving(false);
    }
  };
  // Get time constraints based on weekday and group size
  const getTimeHelperText = (): string => {
    if (!slot) return '';
    
    const date = new Date(slot.date);
    const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    if (dayOfWeek >= 1 && dayOfWeek <= 4) { // Monday to Thursday
      if (formData.minCapacity && formData.minCapacity >= 100) {
        return 'Mo-Do mit 100+ Personen: 11:00-17:00 Uhr möglich';
      } else if (formData.minCapacity && formData.minCapacity >= 50) {
        return 'Mo-Do mit 50+ Personen: 11:00-15:00 Uhr möglich';
      }
      return 'Mo-Do: Standard-Zeiten';
    } else if (dayOfWeek === 5) { // Friday
      return 'Freitag: Nachmittagsfahrten beliebt';
    } else { // Weekend
      return 'Wochenende: Nur ALL IN Premium verfügbar';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {slot ? 'Termin bearbeiten' : 'Neuer Termin'}
      </DialogTitle>
      <DialogContent>
        {saveError && (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => setSaveError(null)}>
            {saveError}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Date (readonly for existing slots) */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Datum"
              value={slot?.displayDate || ''}
              fullWidth
              disabled
              helperText="Datum kann nicht geändert werden"
            />
          </Grid>

          {/* Time */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Zeit"
              type="time"
              value={formData.time || ''}
              onChange={handleChange('time')}
              fullWidth
              required
              error={!!errors.time}
              helperText={errors.time || getTimeHelperText()}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Tour Type - Dynamisch von API geladen */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth required error={!!errors.tourType}>
              <InputLabel>Tour-Typ</InputLabel>
              <Select
                value={formData.tourType || ''}
                label="Tour-Typ"
                onChange={handleChange('tourType')}
                disabled={loadingTourTypes}
              >
                {loadingTourTypes ? (
                  <MenuItem value="" disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} /> Lade Tour-Typen...
                  </MenuItem>
                ) : tourTypes.length > 0 ? (
                  tourTypes.map((type) => (
                    <MenuItem key={type.id} value={type.code || type.name}>
                      {type.name}
                    </MenuItem>
                  ))
                ) : (
                  // Fallback wenn API keine Daten liefert
                  <>
                    <MenuItem value="STANDARD">Standard</MenuItem>
                    <MenuItem value="PREMIUM">Premium</MenuItem>
                    <MenuItem value="ALL_IN_PREMIUM">All In Premium</MenuItem>
                    <MenuItem value="EXCLUSIVE">Exklusiv</MenuItem>
                  </>
                )}
              </Select>
              {errors.tourType && (
                <Alert severity="error" sx={{ mt: 1 }}>{errors.tourType}</Alert>
              )}
            </FormControl>
          </Grid>

          {/* Price per Person */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Preis pro Person"
              type="number"
              value={formData.pricePerPerson || ''}
              onChange={handleChange('pricePerPerson')}
              fullWidth
              required
              error={!!errors.pricePerPerson}
              helperText={errors.pricePerPerson}
              InputProps={{
                startAdornment: <InputAdornment position="start">€</InputAdornment>,
              }}
            />
          </Grid>
          {/* Min Capacity */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Mindestkapazität"
              type="number"
              value={formData.minCapacity || ''}
              onChange={handleChange('minCapacity')}
              fullWidth
              required
              error={!!errors.minCapacity}
              helperText={errors.minCapacity || 'Minimum für Bestätigung'}
              inputProps={{ min: 1, max: 160 }}
            />
          </Grid>

          {/* Max Capacity */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Maximalkapazität"
              type="number"
              value={formData.maxCapacity || ''}
              onChange={handleChange('maxCapacity')}
              fullWidth
              required
              error={!!errors.maxCapacity}
              helperText={errors.maxCapacity || 'Öffentlich max. 140, intern max. 160'}
              inputProps={{ min: 1, max: 160 }}
            />
          </Grid>

          {/* Current Bookings (readonly) */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Aktuelle Buchungen"
              value={formData.currentBookings || 0}
              fullWidth
              disabled
              helperText="Kann hier nicht geändert werden"
              InputProps={{
                endAdornment: <InputAdornment position="end">Personen</InputAdornment>,
              }}
            />
          </Grid>

          {/* Available Capacity (readonly) */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Verfügbare Plätze"
              value={(formData.maxCapacity || 0) - (formData.currentBookings || 0)}
              fullWidth
              disabled
              helperText="Automatisch berechnet"
              InputProps={{
                endAdornment: <InputAdornment position="end">Plätze</InputAdornment>,
              }}
            />
          </Grid>
          {/* Is Blocked */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isBlocked || false}
                  onChange={(e) => setFormData(prev => ({ ...prev, isBlocked: e.target.checked }))}
                  color="error"
                />
              }
              label="Termin blockiert"
            />
            {formData.isBlocked && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Blockierte Termine können nicht gebucht werden
              </Alert>
            )}
          </Grid>

          {/* Is Confirmed (readonly) */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isConfirmed || false}
                  disabled
                  color="success"
                />
              }
              label="Termin bestätigt"
            />
            <Alert severity="info" sx={{ mt: 1 }}>
              {formData.isConfirmed 
                ? 'Mindestkapazität erreicht' 
                : `Noch ${Math.max(0, (formData.minCapacity || 30) - (formData.currentBookings || 0))} Plätze bis zur Bestätigung`}
            </Alert>
          </Grid>

          {/* Online-Freigabe Status (readonly, Anlage v1.4 Abschnitt 2.1 Punkt 12) */}
          <Grid size={{ xs: 12 }}>
            <Alert severity={formData.isReleasedForOnlineBooking === false ? 'warning' : 'success'}>
              {formData.isReleasedForOnlineBooking === false
                ? 'UNTER VORBEHALT: Termin ist online NICHT buchbar (Mindestteilnehmer nicht erreicht, keine manuelle Freigabe). Freigabe über den Weltkugel-Button in der Listenansicht.'
                : formData.isManuallyReleased
                  ? 'Online buchbar (manuell freigegeben)'
                  : 'Online buchbar (Mindestteilnehmerzahl erreicht)'}
            </Alert>
          </Grid>

          {/* T-154 (v1.8.44): Sichtbarkeit im Gästekalender (Termin nicht öffentlich oder Slot nicht verfügbar) */}
          {istUnsichtbarFuerGaeste(slot) && (
            <Grid size={{ xs: 12 }}>
              <Alert severity="error">
                {HINWEIS_UNSICHTBAR} Mit dem Schalter wird er beim Speichern wieder angezeigt; die Online-Freigabe ändert sich
                dadurch nicht, der Termin bleibt ohne Freigabe "unter Vorbehalt" (nur auf Anfrage).
              </Alert>
              <FormControlLabel
                control={
                  <Switch
                    checked={sichtbarMachen}
                    onChange={(e) => setSichtbarMachen(e.target.checked)}
                    color="primary"
                  />
                }
                label="Beim Speichern für Gäste sichtbar machen"
              />
            </Grid>
          )}

          {/* Special Notes */}
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Besondere Hinweise"
              multiline
              rows={3}
              value={formData.specialNotes || ''}
              onChange={handleChange('specialNotes')}
              fullWidth
              helperText="Z.B. Feiertage, besondere Events, etc."
            />
          </Grid>

          {/* Interne Notiz je Termin (Anlage v1.4 Abschnitt 2.7 Punkt 4) */}
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Interne Notiz (nicht kundensichtbar)"
              multiline
              rows={2}
              value={formData.internalNote || ''}
              onChange={handleChange('internalNote')}
              fullWidth
              inputProps={{ maxLength: 500 }}
              helperText="Individuelle Eintragungen je Termin, z.B. kostenloses Storno vereinbart"
            />
          </Grid>

          {/* Waitlist Count (readonly) */}
          {formData.waitlistCount && formData.waitlistCount > 0 && (
            <Grid size={{ xs: 12 }}>
              <Alert severity="info">
                {formData.waitlistCount} Person(en) auf der Warteliste
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={onClose} 
          startIcon={<CancelIcon />}
        >
          Abbrechen
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          color="primary"
          startIcon={<SaveIcon />}
          disabled={saving}
        >
          {saving ? 'Speichern...' : 'Speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(SlotEditor);